import GridViewIcon from "@mui/icons-material/GridView";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import TableRowsIcon from "@mui/icons-material/TableRows";
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useDebouncedEffect, useSyncedRef } from "@react-hookz/web";
import { useState } from "react";
import { useMountDate } from "../../../utils/hooks/use-mount-date";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { useProgramAccess } from "../../program-access/hooks/use-program-access";
import { useProgramFavourites } from "../hooks/use-program-favourites";
import { useProgramOverview } from "../hooks/use-program-overview";
import {
  FILTERS,
  SORTS,
  favouriteItems,
  filterLabels,
  passesFilter,
  resultText,
  searchPrograms,
  sendsThisWeek,
  sortLabels,
  sortMatches,
  summarisePrograms,
} from "../program-landing";
import { FavouritesSection } from "./favourites-section";
import { ProgramCard } from "./program-card";
import { Tile } from "./program-parts";
import { ProgramTable } from "./program-table";

import type { AccessData } from "../../program-access/hooks/use-program-access";
import type { ProgramTree } from "../../program-tree/program-tree";
import type {
  ProgramFilter,
  ProgramOverview,
  ProgramSort,
  ProgramSummary,
} from "../program-landing";
import type { FC } from "react";

const Landing: FC<{ tree: ProgramTree; overviews: ProgramOverview[]; access?: AccessData }> = ({
  tree,
  overviews,
  access,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ProgramFilter>("all");
  const [sort, setSort] = useState<ProgramSort>("updated");
  const [layout, setLayout] = useState<"grid" | "table">("grid");
  const today = useMountDate();
  const { favourites, toggle } = useProgramFavourites();
  const summaries = summarisePrograms(tree, overviews, new Set(favourites), today);
  const search = query.trim();
  const pendo = usePendo();
  const matches = sortMatches(
    searchPrograms(summaries, query).filter(({ summary }) => passesFilter(summary, filter)),
    sort,
  );
  // The count on screen when the event goes, as the debounced callback only changes with the search.
  const shown = useSyncedRef(matches.length);

  // One event when the search stops changing for a second, not one for each key.
  useDebouncedEffect(
    () => {
      if (search) pendo?.track("programs-landing-searched", { results: shown.current });
    },
    [search],
    1000,
  );

  if (!summaries.length) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        No programs here yet.
      </Typography>
    );
  }

  const total = (count: (summary: ProgramSummary) => number): number =>
    summaries.reduce((sum, summary) => sum + count(summary), 0);

  return (
    <Stack spacing={3}>
      <Box
        component="section"
        aria-label="Summary"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 2,
        }}
      >
        <Tile label="Programs" value={summaries.length} />
        <Tile
          label="Active communications"
          value={total(({ active }) => active)}
          color="success.dark"
        />
        <Tile label="Drafts to finish" value={total(({ drafts }) => drafts)} />
        <Tile label="Sending in the next 7 days" value={summaries.filter(sendsThisWeek).length} />
      </Box>

      <FavouritesSection
        favourites={favouriteItems(tree, summaries, favourites)}
        layout={layout}
        today={today}
        onToggle={({ node }) => void toggle(node.id)}
      />

      <Stack component="section" aria-label="Find a program" spacing={1.5}>
        <Stack direction="row" useFlexGap sx={{ alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            size="small"
            type="search"
            placeholder="Search programs and folders"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: "1 1 320px", maxWidth: 480, bgcolor: "background.paper" }}
            slotProps={{
              htmlInput: { "aria-label": "Search programs and folders" },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Stack
            direction="row"
            role="group"
            aria-label="Filter programs"
            useFlexGap
            sx={{ gap: 1, flexWrap: "wrap" }}
          >
            {FILTERS.map((each) => (
              <Chip
                key={each}
                label={
                  <>
                    {filterLabels[each]}{" "}
                    <Box component="span" sx={{ ml: 0.5, fontWeight: 500, opacity: 0.8 }}>
                      {summaries.filter((summary) => passesFilter(summary, each)).length}
                    </Box>
                  </>
                }
                onClick={() => setFilter(each)}
                aria-pressed={each === filter}
                color={each === filter ? "primary" : "default"}
                variant="outlined"
                sx={{ fontWeight: 600 }}
                data-analytics-id={`programs-landing-filter-${each}`}
              />
            ))}
          </Stack>
          <Box sx={{ flex: 1 }} />
          <TextField
            select
            size="small"
            label="Sort by"
            value={sort}
            onChange={(e) => setSort(SORTS.find((each) => each === e.target.value) ?? "updated")}
            sx={{ minWidth: 220, bgcolor: "background.paper" }}
          >
            {SORTS.map((each) => (
              <MenuItem key={each} value={each}>
                {sortLabels[each]}
              </MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={layout}
            onChange={(_, value: "grid" | "table" | null) => {
              if (value) setLayout(value);
            }}
            aria-label="Layout"
            sx={{ bgcolor: "background.paper" }}
          >
            <ToggleButton value="grid" data-analytics-id="programs-landing-layout-grid">
              <GridViewIcon fontSize="small" sx={{ mr: 0.75 }} />
              Grid
            </ToggleButton>
            <ToggleButton value="table" data-analytics-id="programs-landing-layout-table">
              <TableRowsIcon fontSize="small" sx={{ mr: 0.75 }} />
              Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Typography role="status" variant="body2" sx={{ color: "text.secondary" }}>
          {resultText(matches.length, summaries.length, query, filter)}
        </Typography>
      </Stack>

      {matches.length && layout === "table" ? (
        <ProgramTable
          matches={matches}
          tree={tree}
          access={access}
          sort={sort}
          onSort={setSort}
          today={today}
          onToggleFavourite={({ summary }) => void toggle(summary.node.id)}
        />
      ) : matches.length ? (
        <Box
          component="ul"
          aria-label="Programs"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(400px, 100%), 1fr))",
            gap: 2.5,
          }}
        >
          {matches.map(({ summary, folders }) => (
            <Box component="li" key={summary.node.id} sx={{ display: "flex" }}>
              <ProgramCard
                summary={summary}
                tree={tree}
                access={access}
                matched={folders}
                today={today}
                onToggleFavourite={() => void toggle(summary.node.id)}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            borderStyle: "dashed",
            py: 6,
            px: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.25,
            textAlign: "center",
          }}
        >
          <SearchOffIcon aria-hidden sx={{ fontSize: 36, color: "text.disabled" }} />
          <Typography variant="subtitle1" component="p" sx={{ fontWeight: 600 }}>
            {search ? `No programs or folders match “${search}”` : "No programs match this filter"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Try a different word, or search for a folder inside a program.
          </Typography>
          <Button
            onClick={() => {
              setQuery("");
              setFilter("all");
            }}
            data-analytics-id="programs-landing-clear"
          >
            Clear search and filters
          </Button>
        </Paper>
      )}
    </Stack>
  );
};

/** The landing page with each program's access, which only admins can read. */
const LandingWithAccess: FC<{ tree: ProgramTree; overviews: ProgramOverview[] }> = ({
  tree,
  overviews,
}) => {
  const { data } = useProgramAccess();
  return <Landing tree={tree} overviews={overviews} access={data ?? undefined} />;
};

/**
 * The programs landing page: the caller's favourites, then a card or a table row for each top
 * program the caller can open, with counts that include its folders, a search over the names of the
 * programs and their folders, filters and a sort. showAccess shows who can see each program, for
 * admins.
 */
export const ProgramsLanding: FC<{ tree: ProgramTree; showAccess: boolean }> = ({
  tree,
  showAccess,
}) => {
  const { data: overviews, error } = useProgramOverview();

  if (error) return <Alert severity="error">The programs could not be loaded.</Alert>;
  if (!overviews) return <LinearProgress />;

  return showAccess ? (
    <LandingWithAccess tree={tree} overviews={overviews} />
  ) : (
    <Landing tree={tree} overviews={overviews} />
  );
};
